import { Size } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';

import { Character } from '@cartwhl/common/api/getCharacters';
import { FromStore, RotationOffsets } from '@cartwhl/common/types';

import { BONE_DISPLAY_NAMES, MARKERS, ROTATION_LIMITS } from './constants';
import {
  AssignableBone,
  BodyMarker,
  CartwheelBoneKey,
  RetargetingBone,
} from './types';

/**
 * Finds the middle point between the two middle intersections in an array of raycaster intersections.
 * For odd number of intersections, it uses the middle three points to calculate an average.
 * For single intersection, returns that point directly.
 *
 * @param intersects Array of raycaster intersections
 * @returns The calculated middle point or undefined if there are no intersections
 */
export function findMiddleIntersectionPoint(
  intersects: THREE.Intersection[],
): THREE.Vector3 | undefined {
  if (intersects.length === 0) {
    return undefined;
  }

  if (intersects.length === 1) {
    return intersects[0].point;
  }

  // For even number of intersections (e.g., [0,1,2,3] -> use 1,2)
  // For odd number of intersections (e.g., [0,1,2,3,4] -> use 1,2,3)
  const isEven = intersects.length % 2 === 0;
  const middleIndex = Math.floor(intersects.length / 2);

  if (isEven) {
    const point1 = intersects[middleIndex - 1].point;
    const point2 = intersects[middleIndex].point;

    return new THREE.Vector3(
      (point1.x + point2.x) / 2,
      (point1.y + point2.y) / 2,
      (point1.z + point2.z) / 2,
    );
  } else {
    // For odd numbers, use the middle three points
    const point1 = intersects[middleIndex - 1].point;
    const point2 = intersects[middleIndex].point;
    const point3 = intersects[middleIndex + 1].point;

    return new THREE.Vector3(
      (point1.x + point2.x + point3.x) / 3,
      (point1.y + point2.y + point3.y) / 3,
      (point1.z + point2.z + point3.z) / 3,
    );
  }
}

/**
 * Converts body markers to an API payload format.
 *
 * @param bodyMarkers Array of body marker objects
 * @returns API payload with landmarks for each body marker
 */
export function getApiPayload(bodyMarkers: BodyMarker[]) {
  const payload: Record<
    string,
    Record<string, { x: number; y: number; z: number }>
  > = { landmarks: {} };
  bodyMarkers.forEach((item) => {
    item.markers
      .filter((marker) => marker.worldPosition)
      .forEach((marker) => {
        payload.landmarks[marker.id] = {
          x: marker.worldPosition!.x,
          y: marker.worldPosition!.y,
          z: marker.worldPosition!.z,
        };
      });
  });

  return payload;
}

/**
 * Creates a debug helper for visualizing rays in the scene.
 * This is useful for debugging raycasting issues.
 *
 * @param scene The THREE.Scene to add the helper to
 * @param origin The origin point of the ray
 * @param direction The direction vector of the ray
 * @param length The length of the ray
 * @param color The color of the ray
 * @returns The helper object that was added to the scene
 */
export function createRayDebugHelper(
  scene: THREE.Scene,
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  length = 1000,
  color = 0xff0000,
): THREE.ArrowHelper {
  const normalizedDir = direction.clone().normalize();
  const arrowHelper = new THREE.ArrowHelper(
    normalizedDir,
    origin,
    length,
    color,
    length * 0.05, // head length
    length * 0.02, // head width
  );

  // Make sure it's visible and renders on top
  arrowHelper.renderOrder = 999;

  // Handle line material
  if (arrowHelper.line && arrowHelper.line.material) {
    const lineMaterial = arrowHelper.line.material as THREE.Material;
    lineMaterial.depthTest = false;
    lineMaterial.transparent = false;
    if ('opacity' in lineMaterial) {
      lineMaterial.opacity = 1.0;
    }
  }

  // Handle cone material
  if (arrowHelper.cone && arrowHelper.cone.material) {
    const coneMaterial = arrowHelper.cone.material as THREE.Material;
    coneMaterial.depthTest = false;
    coneMaterial.transparent = false;
    if ('opacity' in coneMaterial) {
      coneMaterial.opacity = 1.0;
    }
  }

  scene.add(arrowHelper);
  console.log('Added ray debug helper to scene', {
    origin: [origin.x, origin.y, origin.z],
    direction: [normalizedDir.x, normalizedDir.y, normalizedDir.z],
    length,
    color,
  });

  return arrowHelper;
}

export function createFileFromScene(
  scene: THREE.Group,
  fileName: string,
  fileExtension: 'gltf' | 'glb',
) {
  return new Promise<File>((resolve, reject) => {
    const exporter = new GLTFExporter();

    const type =
      fileExtension === 'glb' ? 'model/gltf-binary' : 'model/gltf+json';

    exporter.parse(
      scene,
      (gltf) => {
        let blob: Blob;
        if (fileExtension === 'glb') {
          blob = new Blob([gltf as ArrayBuffer], { type });
        } else {
          // For GLTF, we need to stringify the JSON data
          const jsonString = JSON.stringify(gltf, null, 2);
          blob = new Blob([jsonString], { type });
        }
        const file = new File([blob], `${fileName}.${fileExtension}`, { type });
        resolve(file);
      },
      (error) => {
        if (error) {
          reject(error);
        }
      },
      {
        binary: fileExtension === 'glb',
        includeCustomExtensions: true,
        maxTextureSize: 4096,
      },
    );
  });
}

/**
 * Finds and maps marker positions from a character mesh
 *
 * Takes a THREE.Group containing the character and returns an array of BodyMarkers with their world positions set.
 * First creates a map of bone names to their world positions by traversing the mesh.
 * Then updates each marker's position by looking up its corresponding bone position.
 *
 * @param scene - The THREE.Group containing the character
 * @returns Array of BodyMarkers with updated world positions
 */
export function findMarkersFromCharacter(scene: THREE.Group): BodyMarker[] {
  const bodyMarkers: BodyMarker[] = JSON.parse(JSON.stringify(MARKERS));

  // Create a map of bone names to their positions
  const bonePositions = new Map<string, THREE.Vector3>();
  scene.traverse((child) => {
    // Check if the child's name exists as a value in the Bones enum
    if (
      Object.values(CartwheelBoneKey).includes(child.name as CartwheelBoneKey)
    ) {
      const worldPosition = new THREE.Vector3();
      child.getWorldPosition(worldPosition);
      bonePositions.set(child.name, worldPosition);
    }
  });

  // Update marker positions by looking up their corresponding bone positions
  bodyMarkers.forEach((markerGroup) => {
    markerGroup.markers.forEach((marker) => {
      const position = bonePositions.get(marker.id);
      if (position) {
        marker.worldPosition = position;
      }
    });
  });

  return bodyMarkers;
}

export function getBonesFromScene(scene?: THREE.Group): THREE.Bone[] {
  if (!scene) return [];

  const bones: THREE.Bone[] = [];
  scene.traverse((child) => {
    if (child instanceof THREE.Bone) {
      bones.push(child);
    }
  });
  return bones;
}

/**
 * Creates an array of RetargetingBone objects from the Bones enum.
 * Each bone is mapped to its corresponding cartwheel bone and assigned bone is set to null.
 *
 * @returns Array of RetargetingBone objects
 */
export function getInitialRetargetingBones(): RetargetingBone[] {
  return Object.values(CartwheelBoneKey).map((bone) => {
    return {
      displayName: BONE_DISPLAY_NAMES[bone],
      cartwheelBoneKey: bone,
      assignedBone: null,
    };
  });
}

/**
 * Finds bones in a scene and maps them to RetargetingBone objects if their names match the cartwheel bone keys.
 * For each bone that matches, it assigns the bone to the RetargetingBone object.
 * The matching is done by checking either the bone's userData.autoAssign property or its name against the cartwheel bone key.
 *
 * @param scene - The THREE.Group containing the character's mesh and skeleton
 * @param retargetingBones - Array of RetargetingBone objects to check for matches against the scene's bones
 * @returns Array of RetargetingBone objects with matched bones assigned
 */
export function getRetargetingBones(
  scene: THREE.Group,
  retargetingBones: RetargetingBone[],
): RetargetingBone[] {
  const bones = getBonesFromScene(scene);
  return retargetingBones.map((retargetingBone) => {
    const matchingBone = bones.find(
      (bone) =>
        (bone.userData.autoAssign ?? bone.name) ===
        retargetingBone.cartwheelBoneKey,
    );
    if (matchingBone) {
      matchingBone.name = matchingBone.userData.autoAssign || matchingBone.name;
    }
    return {
      ...retargetingBone,
      assignedBone: matchingBone || null,
    };
  });
}

/**
 * Gets a list of bones from a scene that can be assigned to cartwheel bones.
 * Each bone is mapped to an AssignableBone object containing the bone and which cartwheel bone it's assigned to (if any).
 *
 * @param mesh - The THREE.Group containing the character's mesh and skeleton
 * @param retargetingBones - Array of RetargetingBone objects to check for existing assignments
 * @returns Array of AssignableBone objects representing assignable bones in the scene
 */
export function getAssignableBones(
  mesh?: THREE.Group,
  retargetingBones?: RetargetingBone[],
): AssignableBone[] {
  if (!mesh) return [];

  const bones = getBonesFromScene(mesh);
  const assignableBones: AssignableBone[] = [];
  bones.forEach((bone) => {
    assignableBones.push({
      bone,
      assignedTo:
        retargetingBones?.find((rb) => rb.assignedBone?.name === bone.name)
          ?.cartwheelBoneKey || null,
    });
  });
  return assignableBones;
}

/**
 * Update camera transform to align itself to a 3d object
 * @param object - 3D object, be it a scene, mesh, group, etc.
 * @param camera - Main camera to apply updates too
 * @param size - Size of the canvas
 * @param padding - Extra padding that can be added to the fitting
 */
export function fitObjectToView(
  object: THREE.Object3D,
  camera: THREE.Camera,
  size: Size,
  padding: number = 1.1,
) {
  const box = getObjectBoundingBox(object);
  const boxSize = box.getSize(new THREE.Vector3());
  const maxDimension = Math.max(boxSize.x, boxSize.y);

  if (camera instanceof THREE.OrthographicCamera) {
    const zoom = Math.min(size.width, size.height) / (maxDimension * padding);
    const v = new THREE.Vector3();
    camera.position.copy(box.getCenter(v));
    camera.position.add(v.set(0, 0, boxSize.z + 0.4));
    camera.zoom = zoom;
    camera.updateProjectionMatrix();
  }
}

/**
 * Compute the bounding box of a 3D object while filtering out things
 * that may artificially increase its size.
 * @param object
 * @returns
 */
export function getObjectBoundingBox(object: THREE.Object3D): THREE.Box3 {
  const defaultLayer = new THREE.Layers();
  defaultLayer.set(0);

  // Create a box that only considers objects in layer 0 (default layer)
  const box = new THREE.Box3();
  object.traverse((child) => {
    switch (child.type) {
      // Filter out objects that can interfere with fitting calculations
      case 'Scene':
      case 'OrthographicCamera':
      case 'SpotLight':
      case 'PointLight':
      case 'DirectionalLight':
      case 'AmbientLight':
      case 'Bone':
      case 'Group':
      case 'Object3D':
        break;

      default:
        // Only include objects in the default layer (layer 0)
        // While avoiding screen space objects thats often set to renderOrder 100
        // Also avoid any negative renderOrders
        if (
          child.renderOrder >= 0 &&
          child.renderOrder < 100 &&
          child.layers.test(defaultLayer)
        ) {
          box.expandByObject(child);
        }
    }
  });

  return box;
}

/**
 * Rotation offsets helpers:
 */
export function createRotationOffsets(
  headValue: number,
  collarValue: number,
  hipValue: number,
  shoulderValue: number,
  ankleValue: number,
): RotationOffsets {
  // Clamp incoming values to respect global rotation limits
  const clampedHead = Math.max(
    Math.min(headValue, ROTATION_LIMITS.X_HEAD.MAX),
    ROTATION_LIMITS.X_HEAD.MIN,
  );
  const clampedCollar = Math.max(
    Math.min(collarValue, ROTATION_LIMITS.Z_COLLAR.MAX),
    ROTATION_LIMITS.Z_COLLAR.MIN,
  );
  const clampedHip = Math.max(
    Math.min(hipValue, ROTATION_LIMITS.Z_HIP.MAX),
    ROTATION_LIMITS.Z_HIP.MIN,
  );
  const clampedShoulder = Math.max(
    Math.min(shoulderValue, ROTATION_LIMITS.Z_SHOULDER.MAX),
    ROTATION_LIMITS.Z_SHOULDER.MIN,
  );
  const clampedAnkle = Math.max(
    Math.min(ankleValue, ROTATION_LIMITS.Y_ANKLE.MAX),
    ROTATION_LIMITS.Y_ANKLE.MIN,
  );

  // Apply the same axis conventions used in the set*Rotation helpers above:
  // - head: X axis (index 0)
  // - collar/hip/shoulder: Z axis (index 2), mirrored left/right on Z
  // - ankle: Y axis (index 1), mirrored left/right on Y
  const head: [number, number, number] = [clampedHead, 0, 0];
  const neck: [number, number, number] = [0, 0, 0];
  const leftCollar: [number, number, number] = [0, 0, clampedCollar];
  const rightCollar: [number, number, number] = [0, 0, -clampedCollar];
  const leftHip: [number, number, number] = [0, 0, clampedHip];
  const rightHip: [number, number, number] = [0, 0, -clampedHip];
  const leftShoulder: [number, number, number] = [0, 0, clampedShoulder];
  const rightShoulder: [number, number, number] = [0, 0, -clampedShoulder];
  const leftAnkle: [number, number, number] = [0, clampedAnkle, 0];
  const rightAnkle: [number, number, number] = [0, -clampedAnkle, 0];

  return {
    head,
    neck,
    left_collar: leftCollar,
    right_collar: rightCollar,
    left_hip: leftHip,
    right_hip: rightHip,
    left_shoulder: leftShoulder,
    right_shoulder: rightShoulder,
    left_ankle: leftAnkle,
    right_ankle: rightAnkle,
  };
}

/**
 * Gets the current value of a rotation offset for a character
 * @param joint The joint to get the rotation for ('head', 'collar', 'hip', 'shoulder', or 'ankle')
 * @returns The rotation value (always positive, representing the left side value)
 */
export function getRotationOffset(
  character?: FromStore<Character>,
  joint?: 'head' | 'collar' | 'hip' | 'shoulder' | 'ankle',
): number {
  if (!character) return 0;

  switch (joint) {
    case 'head':
      return character.rotationOffsets?.head?.[0] ?? 0;
    case 'collar':
      return character.rotationOffsets?.left_collar?.[2] ?? 0;
    case 'hip':
      return character.rotationOffsets?.left_hip?.[2] ?? 0;
    case 'shoulder':
      return character.rotationOffsets?.left_shoulder?.[2] ?? 0;
    case 'ankle':
      return character.rotationOffsets?.left_ankle?.[1] ?? 0;
    default:
      return 0;
  }
}
